import "regenerator-runtime"; // runtime for async and generator functions
import "./other/devArea/legacyRedDeer.css";
import { Container, containerContext } from "./other/injection";
import { RootProvider, ITheme } from "./other/theme";
import { configure } from "mobx";
import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Global, css } from "@emotion/react";
import {
    AngularLoaderStore,
    preloadableScriptUrls,
    preloadableStyleUrls
} from "./other/bridgeSetup/AngularLoaderStore";
import { HashRouter } from "react-router-dom";
import { AppSkeleton } from "./other/appSkeleton/AppSkeleton";
import { AppTimerService } from "./other/timer/AppTimerService";
import { MessageBusService } from "./other/messageBus/MessageBusService";
import { ConnectionStatus } from "./other/messageBus/ConnectionStatus";
import { TimeoutNotice } from "./other/messageBus/TimeoutNotice";
import { ErrorStore } from "./other/error/ErrorStore";
import { ErrorDisplay } from "./other/error/ErrorDisplay";
import { AnalyticsService } from "./other/analytics/AnalyticsService";
import { createBridges } from "./other/bridgeSetup/createBridges";
import { windowAsAny } from "./other/windowAsAny/windowAsAny";
import { Roboto } from "./other/roboto/Roboto";
import { AccessOverlay } from "./other/navigationAndRouting/AccessOverlay";
import "./index.css";

// configure mobx
configure({
    enforceActions: "always",
    computedRequiresReaction: true
});

// setup dependency injection container
const container = new Container();
container.allowAutoInjection = true;
container.set(Container, container); // allowing discovery of the container is general bad practise, but it is needed in rare circumstances to avoid circular dependencies

// setup global error handler
// it's used for interoperability between angular & react code
container.get(ErrorStore).setupErrorHandlers();

// create bridges
createBridges(container);

// start app timer
container.get(AppTimerService);

// additional styles
const additionalGlobalStyles = (_: ITheme) =>
    css({
        html: {
            overflow: "hidden",
            width: "100%",
            height: "100%"
        },
        body: {
            width: "100%",
            height: "100%"
        }
    });

// if loading inside main reddeer app
// no visible components are rendered on load, but some global css needs to be added
// also, need to setup the message bus (message bus code is now in this react codebase)
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
if (!windowAsAny().isRedDeerReactFirst) {
    const Index = () => {
        // RootProvider contains some default global css styles
        return (
            <containerContext.Provider value={container}>
                <RootProvider>
                    <Roboto />
                    <Global styles={additionalGlobalStyles} />
                </RootProvider>
            </containerContext.Provider>
        );
    };

    window.addEventListener("DOMContentLoaded", () => {
        const div = document.createElement("div");
        document.getElementsByTagName("body")[0]?.appendChild(div);
        const root = createRoot(div);
        root.render(<Index />);
    });

    container.get(MessageBusService).getConnectedClient();
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
const AnyHashRouter = HashRouter as any; // react version type issue

// do not preload angular if running in dev server - this allows quicker dev iteration when not working on an angular page
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
const disableAngularPreload = windowAsAny().isRedDeerDevServer ?? false;

// if react first app
// setup error handlers, add additional body styles, render app, start analytics and start preloading
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
if (windowAsAny().isRedDeerReactFirst) {
    const Index = () => {
        return (
            <>
                <containerContext.Provider value={container}>
                    <RootProvider>
                        <Roboto />
                        <Global styles={additionalGlobalStyles} />
                        <AnyHashRouter>
                            {/* top level Suspense to prevent errors. ideally suspense will be caught before this */}
                            <Suspense fallback={null}>
                                <AppSkeleton />
                                <AccessOverlay />
                                <ConnectionStatus />
                                <TimeoutNotice />
                                <ErrorDisplay />
                            </Suspense>
                        </AnyHashRouter>
                    </RootProvider>
                </containerContext.Provider>
                {
                    // if not already preloaded angular scripts and styles
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    !windowAsAny().isRedDeerModuleLoader &&
                        !disableAngularPreload && (
                            <>
                                {preloadableStyleUrls().map((x, i) => (
                                    <link
                                        rel="preload"
                                        as="style"
                                        href={x}
                                        key={i}
                                    />
                                ))}
                                {preloadableScriptUrls().map((x, i) => (
                                    <link
                                        rel="preload"
                                        as="script"
                                        href={x}
                                        key={i}
                                    />
                                ))}
                            </>
                        )
                }
            </>
        );
    };
    const index = document.getElementById("index");
    if (index) {
        const root = createRoot(index);
        root.render(<Index />);
    }

    // start connecting to message bus
    container.get(MessageBusService).getConnectedClient();

    // analytics
    container.get(AnalyticsService).identifyUser();

    // preload angular app
    // timeout of 0 = angular starts loaded immediately which will cause a small delay in first paint
    // timeout of 500 = allow first paint before loading angular
    // timeout of 2000 = will leave enough time for lighthouse to consider the page loaded (useful for testing initial load performance)
    if (!disableAngularPreload) {
        setTimeout(() => {
            void container.get(AngularLoaderStore).loader();
        }, 500);
    }
}
